import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
  ActionButton,
  Popover,
  Text,
  ToastQueue,
  mergeStyles,
} from "@react-spectrum/s2";
import { useHover } from "@react-aria/interactions";
import { useFocusRing } from "react-aria";
import CopyIcon from "@react-spectrum/s2/icons/Copy";

import {
  style,
  focusRing,
} from "@react-spectrum/s2/style" with { type: "macro" };

// How long the popover waits to close, so the pointer can cross the gap
// between the section and the popover.
const CLOSE_DELAY = 150;

const wrapperStyle = style({
  ...focusRing(),
  borderRadius: "default",
});

// Presses and keys in the popover must not reach what the section lives in
// (a card, say) — the popover is its child in the React tree. Escape is left
// to close the popover.
const stopPropagation = (e) => e.stopPropagation();
const stopKeyPropagation = (e) => {
  if (e.key !== "Escape") {
    e.stopPropagation();
  }
};

/**
 * A preview section — `children`, such as a preview link — which, when a
 * `password` is given, is focusable and shows it in a popover, without delay,
 * while hovered or focused, with a button to copy it. Without a password it is
 * just a wrapper. `styles` lay out the wrapper; `onOpenChange(isOpen)` follows
 * the popover.
 */
export const PreviewPasswordPopover = ({
  password,
  children,
  styles,
  label = "Preview",
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const closeTimer = useRef(null);

  const setOpen = (value) => {
    setIsOpen(value);
    onOpenChange?.(value);
  };

  const open = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const { hoverProps: triggerHoverProps } = useHover({
    onHoverStart: open,
    onHoverEnd: scheduleClose,
  });
  const { hoverProps: popoverHoverProps } = useHover({
    onHoverStart: open,
    onHoverEnd: scheduleClose,
  });
  const { focusProps, isFocusVisible } = useFocusRing({ within: false });

  const hasPassword = typeof password === "string" && !!password;

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(password);
      ToastQueue.positive("Password copied", { timeout: 3000 });
    } catch {
      ToastQueue.negative("Could not copy the password", { timeout: 5000 });
    }
  };

  return (
    <>
      <div
        ref={triggerRef}
        {...(hasPassword
          ? {
              ...triggerHoverProps,
              ...focusProps,
              tabIndex: 0,
              role: "group",
              "aria-label": label,
              onFocus: (e) => {
                focusProps.onFocus?.(e);
                open();
              },
              onBlur: (e) => {
                focusProps.onBlur?.(e);
                scheduleClose();
              },
            }
          : {})}
        className={mergeStyles(
          wrapperStyle({ isFocusVisible: hasPassword && isFocusVisible }),
          styles,
        )}
      >
        {children}
      </div>
      {hasPassword && (
        <Popover
          triggerRef={triggerRef}
          isOpen={isOpen}
          onOpenChange={setOpen}
          placement="top"
          hideArrow
          isNonModal
        >
          <div
            {...popoverHoverProps}
            onFocus={open}
            onBlur={scheduleClose}
            onPointerDown={stopPropagation}
            onMouseDown={stopPropagation}
            onClick={stopPropagation}
            onKeyDown={stopKeyPropagation}
            onKeyUp={stopKeyPropagation}
            className={style({
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: 12,
              font: "ui",
            })}
          >
            <Text styles={style({ font: "detail" })}>
              Password:{" "}
              <Text styles={style({ font: "ui", marginStart: 4 })}>
                {password}
              </Text>
            </Text>
            <ActionButton
              aria-label="Copy password"
              isQuiet
              size="S"
              onPress={copyPassword}
            >
              <CopyIcon />
            </ActionButton>
          </div>
        </Popover>
      )}
    </>
  );
};

PreviewPasswordPopover.propTypes = {
  password: PropTypes.string,
  children: PropTypes.node,
  styles: PropTypes.string,
  label: PropTypes.string,
  onOpenChange: PropTypes.func,
};
